import { ref } from 'vue';
import { httpService } from '../services/httpService';
import { log } from '../utils/DebugLogger';
import { validate } from '../contracts/validate';
import '../contracts/CompromissosServicos_SalvaCompromissoServicoInput';
import '../contracts/CompromissosServicos_SalvaNovoCompromissoServicoInput';
import type { CompromissosServicos_GetbyidResponse } from '../contracts/CompromissosServicos_GetbyidResponse';
import '../contracts/CompromissosServicos_GetbyidResponse';
import '../contracts/CompromissosServicos_SalvaCompromissoServicoResponse';
import '../contracts/CompromissosServicos_SalvaNovoCompromissoServicoResponse';
import '../contracts/CompromissosServicos_CancelaCompromissoServicoResponse';
import '../contracts/CompromissosServicos_RemoveCompromissoServicoResponse';
import '../contracts/OrcamentosServicos_BuscaOrcamentoServicosPorOrcamentoIDResponse';
import '../contracts/OrcamentosServicos_SelectserviciosResponse';
import '../contracts/Agenda_AdicionaOrcamentoServicoNoCompromissoInput';
import '../contracts/Agenda_AdicionaOrcamentoServicoNoCompromissoResponse';

interface ApiResponse<T = unknown> {
  success: boolean;
  dados?: T;
  data?: T;
  error?: string;
  message?: string;
}

interface CompromissoServicoRow {
  compromisso_servico_id?: number;
  [key: string]: unknown;
}

interface OrcamentoServicoRow {
  orcamento_servico_id?: number;
  [key: string]: unknown;
}

interface ServicoSelectRow {
  servico_id?: number;
  [key: string]: unknown;
}

/**
 * Composable for Compromisso Servico (Appointment Service) operations
 * Provides methods to interact with the Compromissos Servicos API
 */
export function useCompromissoServico() {
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  /**
   * Fetch single compromisso servico by ID
   */
  const fetchCompromissoServico = async (compromissoServicoId: number) => {
    log('useCompromissoServico', 'fetchCompromissoServico', 'Fetching compromisso servico', compromissoServicoId);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get<ApiResponse>(
        `/index.php/compromissos_servicos/getByID/${compromissoServicoId}`,
        { responseContract: 'CompromissosServicos_GetbyidResponse' }
      );
      log('useCompromissoServico', 'fetchCompromissoServico', 'Response', response);

      if (response.success) {
        return response.dados;
      } else {
        throw new Error(response.error || 'Erro ao buscar compromisso servico');
      }
    } catch (err) {
      log('useCompromissoServico', 'fetchCompromissoServico', 'Error', err);
      error.value = err instanceof Error ? err.message : String(err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Save existing compromisso servico
   */
  const saveCompromissoServico = async (compromissoServicoData: Record<string, unknown>) => {
    log('useCompromissoServico', 'saveCompromissoServico', 'Saving compromisso servico', compromissoServicoData);
    isLoading.value = true;
    error.value = null;

    try {
      validate('CompromissosServicos_SalvaCompromissoServicoInput', compromissoServicoData);
      const response = await httpService.post<ApiResponse>('/index.php/compromissos_servicos/salvaCompromissoServico', compromissoServicoData, {
        responseContract: 'CompromissosServicos_SalvaCompromissoServicoResponse',
        inputContract: 'CompromissosServicos_SalvaCompromissoServicoInput'
      });
      log('useCompromissoServico', 'saveCompromissoServico', 'Response', response);

      if (response.success) {
        return response;
      } else {
        throw new Error(response.message || response.error || 'Erro ao salvar compromisso servico');
      }
    } catch (err) {
      log('useCompromissoServico', 'saveCompromissoServico', 'Error', err);
      error.value = err instanceof Error ? err.message : String(err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Save new compromisso servico
   */
  const saveNovoCompromissoServico = async (novoServicoData: Record<string, unknown>) => {
    log('useCompromissoServico', 'saveNovoCompromissoServico', 'Saving new compromisso servico', novoServicoData);
    isLoading.value = true;
    error.value = null;

    try {
      validate('CompromissosServicos_SalvaNovoCompromissoServicoInput', novoServicoData);
      const response = await httpService.post<ApiResponse>('/index.php/compromissos_servicos/salvaNovoCompromissoServico', novoServicoData, {
        responseContract: 'CompromissosServicos_SalvaNovoCompromissoServicoResponse',
        inputContract: 'CompromissosServicos_SalvaNovoCompromissoServicoInput'
      });
      log('useCompromissoServico', 'saveNovoCompromissoServico', 'Response', response);

      if (response.success) {
        return response;
      } else {
        throw new Error(response.message || response.error || 'Erro ao salvar novo compromisso servico');
      }
    } catch (err) {
      log('useCompromissoServico', 'saveNovoCompromissoServico', 'Error', err);
      error.value = err instanceof Error ? err.message : String(err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Cancel compromisso servico (sets status to CANCELADO)
   */
  const cancelCompromissoServico = async (compromissoServicoId: number) => {
    log('useCompromissoServico', 'cancelCompromissoServico', 'Cancelling', compromissoServicoId);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get<ApiResponse>(
        `/index.php/compromissos_servicos/cancelaCompromissoServico/${compromissoServicoId}`,
        { responseContract: 'CompromissosServicos_CancelaCompromissoServicoResponse' }
      );
      log('useCompromissoServico', 'cancelCompromissoServico', 'Response', response);

      if (response.success) {
        return response;
      } else {
        throw new Error(response.error || 'Erro ao cancelar compromisso servico');
      }
    } catch (err) {
      log('useCompromissoServico', 'cancelCompromissoServico', 'Error', err);
      error.value = err instanceof Error ? err.message : String(err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Remove (delete) compromisso servico record
   */
  const removeCompromissoServico = async (compromissoServicoId: number) => {
    log('useCompromissoServico', 'removeCompromissoServico', 'Removing', compromissoServicoId);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get<ApiResponse>(
        `/index.php/compromissos_servicos/removeCompromissoServico/${compromissoServicoId}`,
        { responseContract: 'CompromissosServicos_RemoveCompromissoServicoResponse' }
      );
      log('useCompromissoServico', 'removeCompromissoServico', 'Response', response);

      if (response.success) {
        return response;
      } else {
        throw new Error(response.error || 'Erro ao remover compromisso servico');
      }
    } catch (err) {
      log('useCompromissoServico', 'removeCompromissoServico', 'Error', err);
      error.value = err instanceof Error ? err.message : String(err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Fetch all available services for selection
   */
  const selectServicos = async () => {
    log('useCompromissoServico', 'selectServicos', 'Fetching available services');
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get<ApiResponse<ServicoSelectRow[]>>('/index.php/orcamentos_servicos/selectServicos', {
        responseContract: 'OrcamentosServicos_SelectserviciosResponse',
      });
      log('useCompromissoServico', 'selectServicos', 'Response', response);

      if (response.success) {
        return response.dados || [];
      } else {
        throw new Error(response.error || 'Erro ao buscar serviços');
      }
    } catch (err) {
      log('useCompromissoServico', 'selectServicos', 'Error', err);
      error.value = err instanceof Error ? err.message : String(err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Fetch orcamento services by orcamento ID
   */
  const buscaOrcamentoServicosPorOrcamentoID = async (orcamentoId: number) => {
    log('useCompromissoServico', 'buscaOrcamentoServicosPorOrcamentoID', 'Fetching orcamento services', orcamentoId);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get<ApiResponse<OrcamentoServicoRow[]>>(
        `/index.php/orcamentos_servicos/buscaOrcamentoServicosPorOrcamentoID/${orcamentoId}`,
        { responseContract: 'OrcamentosServicos_BuscaOrcamentoServicosPorOrcamentoIDResponse' }
      );
      log('useCompromissoServico', 'buscaOrcamentoServicosPorOrcamentoID', 'Response', response);

      if (response.success) {
        return response.dados || [];
      } else {
        throw new Error(response.error || 'Erro ao buscar serviços do orçamento');
      }
    } catch (err) {
      log('useCompromissoServico', 'buscaOrcamentoServicosPorOrcamentoID', 'Error', err);
      error.value = err instanceof Error ? err.message : String(err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Add orcamento servicos (still "aguardando agendamento") to an existing compromisso.
   * Backend: POST /agenda/adiciona_orcamento_servico_no_compromisso — creates one
   * compromissos_servicos row per item (status 3), moves each orcamento_servico to
   * status 3 and recalculates the compromisso total. The previous URL
   * (orcamentos_servicos/adicionarOrcamentoServicoAoCompromisso) never existed (P0-7).
   */
  const adicionarOrcamentoServicoAoCompromisso = async (inputData: Record<string, unknown>) => {
    log('useCompromissoServico', 'adicionarOrcamentoServicoAoCompromisso', 'Adding orcamento servico to compromisso', inputData);
    isLoading.value = true;
    error.value = null;

    try {
      validate('Agenda_AdicionaOrcamentoServicoNoCompromissoInput', inputData);
      const response = await httpService.post<ApiResponse>('/index.php/agenda/adiciona_orcamento_servico_no_compromisso', inputData, {
        responseContract: 'Agenda_AdicionaOrcamentoServicoNoCompromissoResponse',
        inputContract: 'Agenda_AdicionaOrcamentoServicoNoCompromissoInput'
      });
      log('useCompromissoServico', 'adicionarOrcamentoServicoAoCompromisso', 'Response', response);

      if (response.success) {
        return response;
      } else {
        throw new Error(response.message || response.error || 'Erro ao adicionar serviço ao compromisso');
      }
    } catch (err) {
      log('useCompromissoServico', 'adicionarOrcamentoServicoAoCompromisso', 'Error', err);
      error.value = err instanceof Error ? err.message : String(err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  return {
    // State
    isLoading,
    error,

    // Methods
    fetchCompromissoServico,
    saveCompromissoServico,
    saveNovoCompromissoServico,
    cancelCompromissoServico,
    removeCompromissoServico,
    selectServicos,
    buscaOrcamentoServicosPorOrcamentoID,
    adicionarOrcamentoServicoAoCompromisso
  };
}
